import dayjs from 'dayjs';
import type { User } from '../types';
import type { Reagent } from '../types';
import type { Consumable } from '../pages/ConsumableStock';
import type { ExperimentRecord } from '../services/experimentRecords/experimentRecordService';
import type { Literature } from '../services/literature/literatureService';
import type { DutySchedule, DutyDay, DutyGroup } from '../services/dutyScheduleService';
import type {
  ActivityLog,
  LabActivityItem,
  LabTeamParticipationRow,
  Reservation,
  UsageStats
} from '../services/api';
import type { LiteratureNotification } from '../services/literature/notificationService';
import type {
  FeedbackPost,
  FeedbackComment,
  FeedbackTicket,
  FeedbackPostListResult,
  FeedbackCommentListResult
} from '../services/feedback/feedbackService';
import type { ApprovalApplicationExtended } from '../types/approval';
import type { SharedExperimentNotebookFolder } from '../services/experimentRecords/sharedExperimentNotebookService';

export const DEMO_TEAM_ID = 'demo-team';

const iso = (d: dayjs.Dayjs) => d.toISOString();

export function getDemoMembers(): User[] {
  const base = (i: number, partial: Partial<User>): User => ({
    _id: `demo-user-${i}`,
    username: `demo${i}`,
    role: i === 0 ? '管理员' : '普通用户',
    name: partial.name || `演示成员${i}`,
    gender: partial.gender || '男',
    studentId: `D2024${String(i).padStart(3, '0')}`,
    phone: `1380000${String(8000 + i).slice(-4)}`,
    email: `member${i}@demo.lab`,
    supervisor: i < 3 ? '张文华 教授' : '李薇 副教授',
    enrollmentYear: 2023,
    studentType: partial.studentType || '研究生',
    teamId: DEMO_TEAM_ID
  });
  return [
    base(1, { name: '陈思', studentType: '博士' }),
    base(2, { name: '王浩', gender: '男' }),
    base(3, { name: '李薇', gender: '女', studentType: '博士后' }),
    base(4, { name: '刘洋', studentType: '研究生' }),
    base(5, { name: '赵敏', gender: '女', studentType: '研究生' }),
    base(6, { name: '张文华', studentType: '教师', role: '管理员' })
  ];
}

export function getDemoConsumables(): Consumable[] {
  return [
    {
      key: 'demo-c-1',
      _id: 'demo-c-1',
      image: '',
      name: '15 mL 离心管',
      model: '无菌锥底',
      unit: '袋',
      quantity: 48,
      location: '库房 A-12',
      remark: '细胞培养常用',
      buyDate: '2025-11-02',
      receiver: '陈思',
      restockThreshold: 20
    },
    {
      key: 'demo-c-2',
      _id: 'demo-c-2',
      image: '',
      name: '移液枪头 200μL',
      model: '盒装 96 支',
      unit: '盒',
      quantity: 6,
      location: '库房 B-03',
      remark: '低库存关注',
      buyDate: '2025-12-18',
      receiver: '王浩',
      restockThreshold: 15
    },
    {
      key: 'demo-c-3',
      _id: 'demo-c-3',
      image: '',
      name: '细胞培养皿 100mm',
      model: 'TC 处理',
      unit: '箱',
      quantity: 22,
      location: '细胞房储物柜',
      remark: '',
      buyDate: '2026-01-08',
      receiver: '李薇',
      restockThreshold: 10
    }
  ];
}

export function getDemoReagents(): Reagent[] {
  return [
    {
      _id: 'demo-r-1',
      key: 'demo-r-1',
      cas: '23491-52-3',
      vendor: 'Sigma',
      spec: '10 mg',
      unit: '支',
      quantity: 5,
      restockThreshold: 2,
      location: '试剂柜 C1',
      name: 'DAPI 染液',
      buyDate: '2025-10-01',
      receiver: '陈思',
      purchaser: '库房'
    },
    {
      _id: 'demo-r-2',
      key: 'demo-r-2',
      cas: '50-78-2',
      vendor: '国药',
      spec: 'AR 500g',
      unit: '瓶',
      quantity: 12,
      restockThreshold: 3,
      location: '试剂柜 C2',
      name: '阿司匹林',
      buyDate: '2025-08-20',
      receiver: '王浩',
      purchaser: '赵敏'
    },
    {
      _id: 'demo-r-3',
      key: 'demo-r-3',
      cas: '67-64-1',
      vendor: 'Fisher',
      spec: 'HPLC 级 4L',
      unit: '桶',
      quantity: 4,
      restockThreshold: 1,
      location: '有机溶剂间',
      name: '丙酮',
      buyDate: '2026-02-01',
      receiver: '刘洋',
      purchaser: '库房'
    }
  ];
}

function demoAuthor(): ExperimentRecord['createdBy'] {
  return { _id: 'demo-user-1', username: 'chensi', name: '陈思', email: 'chensi@demo.lab' };
}

export function getDemoExperimentRecords(): ExperimentRecord[] {
  const t = dayjs();
  return [
    {
      _id: 'demo-er-1',
      teamId: DEMO_TEAM_ID,
      title: '共聚焦成像：线粒体形态观察',
      summary: '使用 MitoTracker 标记 Hela 细胞，488/561 nm 通道采集；初步观察到应激条件下网络碎片化趋势。',
      discipline: 'biology',
      status: 'final',
      content: '<p>演示数据：正文支持富文本，实际团队内可关联仪器预约与文献。</p>',
      procedureNotes: '细胞密度约 70% 时换液标记，避光孵育 30 min。',
      tags: ['成像', '线粒体'],
      attachments: [],
      createdBy: demoAuthor(),
      updatedBy: demoAuthor(),
      createdAt: iso(t.subtract(2, 'day')),
      updatedAt: iso(t.subtract(5, 'hour'))
    },
    {
      _id: 'demo-er-2',
      teamId: DEMO_TEAM_ID,
      title: 'Suzuki 偶联反应条件筛选',
      summary: 'Pd(PPh₃)₄ / K₂CO₃ 体系，对比二噁烷与甲苯作溶剂对收率的影响。',
      discipline: 'chemistry',
      status: 'draft',
      content: '<p>演示草稿记录。</p>',
      chemistry: {
        objective: '优化芳基硼酸与芳卤偶联条件',
        procedureOutline: '氮气保护下加料，回流 8 h，TLC 监测',
        reagentsAndSolvents: 'Ar-Br、Ar-B(OH)₂、K₂CO₃、Pd 催化剂',
        conditions: '110 °C，二噁烷',
        observations: '甲苯体系副产物略多',
        workupAndAnalysis: '柱层析分离，¹H NMR 确认',
        reagentRows: [
          { compound: '4-溴甲苯', molarity: '—', volume: '5 mmol' },
          { compound: '苯硼酸', molarity: '—', volume: '5.5 mmol' }
        ]
      },
      procedureNotes: '',
      tags: ['有机合成', 'Suzuki'],
      attachments: [],
      createdBy: demoAuthor(),
      updatedBy: demoAuthor(),
      createdAt: iso(t.subtract(9, 'day')),
      updatedAt: iso(t.subtract(1, 'day'))
    },
    {
      _id: 'demo-er-3',
      teamId: DEMO_TEAM_ID,
      title: '材料拉伸样条制备与编号',
      summary: '注塑样条去毛刺后打标入库，对应委托单 #W-2026-014。',
      discipline: 'materials',
      status: 'final',
      content: '<p>演示数据。</p>',
      procedureNotes: '按 SOP-ME-03 执行。',
      tags: ['注塑', '力学'],
      attachments: [],
      createdBy: { _id: 'demo-user-2', username: 'wanghao', name: '王浩', email: 'wanghao@demo.lab' },
      updatedBy: { _id: 'demo-user-2', username: 'wanghao', name: '王浩', email: 'wanghao@demo.lab' },
      createdAt: iso(t.subtract(14, 'day')),
      updatedAt: iso(t.subtract(3, 'day'))
    }
  ];
}

export function isDemoExperimentRecordId(id: string | undefined): boolean {
  return Boolean(id && id.startsWith('demo-er-'));
}

export function getDemoExperimentRecordById(id: string): ExperimentRecord | null {
  return getDemoExperimentRecords().find((r) => r._id === id) || null;
}

export function getDemoLiteratures(): Literature[] {
  const t = iso(dayjs());
  return [
    {
      _id: 'demo-lit-1',
      title: 'Single-cell transcriptomics meets organoids',
      authors: ['Smith, J.', 'Chen, L.'],
      journal: 'Nature Reviews Genetics',
      year: 2024,
      doi: '10.1038/s41576-024-00000-0',
      abstract: '演示文献条目：综述类单细胞与类器官结合的研究范式。',
      readStatus: '阅读中',
      isFavorited: true,
      notes: '<p>重点：技术路线与样本质控。</p>',
      categoryId: undefined,
      createdAt: t,
      updatedAt: t
    },
    {
      _id: 'demo-lit-2',
      title: 'Engineering T cells for solid tumors',
      authors: ['Wang, H.', 'Lee, K.'],
      journal: 'Cell',
      year: 2023,
      readStatus: '已读',
      isFavorited: false,
      notes: '',
      createdAt: t,
      updatedAt: t
    },
    {
      _id: 'demo-lit-3',
      title: '实验室信息管理系统在高校科研中的实践',
      authors: ['课题组联合'],
      journal: '实验技术与管理',
      year: 2025,
      readStatus: '未读',
      isFavorited: false,
      createdAt: t,
      updatedAt: t
    }
  ];
}

export function filterDemoLiteratures(
  list: Literature[],
  categoryId: string | undefined,
  keyword: string
): Literature[] {
  let rows = [...list];
  const q = keyword.trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        (r.authors || []).some((a) => a.toLowerCase().includes(q)) ||
        (r.journal || '').toLowerCase().includes(q)
    );
  }
  if (categoryId === 'unfiled') {
    rows = rows.filter((r) => !r.categoryId);
  } else if (categoryId) {
    rows = rows.filter((r) => r.categoryId === categoryId);
  }
  return rows;
}

export function buildDemoDutySchedule(week: string, weekStartDate: string, weekEndDate: string): DutySchedule {
  const members = getDemoMembers().slice(0, 4);
  const mkDay = (offset: number): DutyDay => {
    const d = dayjs(weekStartDate).add(offset, 'day');
    return {
      date: d.format('YYYY-MM-DD'),
      dayOfWeek: d.day(),
      location: '主实验室',
      members: [
        {
          userId: { _id: members[offset % members.length]._id!, name: members[offset % members.length].name! },
          dutyType: '值日',
          tasks: ['台面清洁', '废液检查', '冰箱温度记录'],
          status: offset < 2 ? '已完成' : '待完成',
          notes: offset === 1 ? '已拍照上传记录' : undefined
        }
      ]
    };
  };
  return {
    _id: 'demo-duty-schedule',
    week,
    weekStartDate,
    weekEndDate,
    schedule: [mkDay(0), mkDay(1), mkDay(2)],
    isActive: true,
    autoRotate: false,
    notificationSettings: { remindBeforeDay: 1, remindOnDay: true },
    createdBy: { _id: 'demo-user-0', name: '演示排班', username: 'demo' },
    createdAt: iso(dayjs()),
    updatedAt: iso(dayjs())
  };
}

export function getDemoUsageStats(period: 'week' | 'month' | 'year'): UsageStats {
  const end = dayjs();
  const start = period === 'week' ? end.subtract(6, 'day') : period === 'month' ? end.subtract(29, 'day') : end.subtract(364, 'day');
  const timeline = Array.from({ length: period === 'week' ? 7 : period === 'month' ? 8 : 6 }).map((_, i) => {
    const d = start.add(i * (period === 'year' ? 60 : period === 'month' ? 4 : 1), 'day');
    return {
      date: d.format('YYYY-MM-DD'),
      durationMinutes: 60 + (i % 4) * 45,
      count: 1 + (i % 3)
    };
  });
  const totalDurationMinutes = timeline.reduce((s, x) => s + x.durationMinutes, 0);
  return {
    period,
    range: { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') },
    totalDurationMinutes,
    totalCount: timeline.reduce((s, x) => s + x.count, 0),
    averageDurationMinutes: Math.round(totalDurationMinutes / Math.max(1, timeline.length)),
    instrumentBreakdown: [
      { instrumentId: 'demo-ins-1', instrumentName: '共聚焦显微镜', durationMinutes: 320, count: 4 },
      { instrumentId: 'demo-ins-2', instrumentName: '流式细胞仪', durationMinutes: 180, count: 2 }
    ],
    timeline
  };
}

export function getDemoConsumableProcurement(): {
  applications: ApprovalApplicationExtended[];
  statusMap: Record<string, string>;
} {
  const key1 = 'demo-proc-c-1';
  const key2 = 'demo-proc-c-2';
  const now = new Date().toISOString();
  const applications: ApprovalApplicationExtended[] = [
    {
      key: key1,
      title: '补充枪头与离心管',
      category: '耗材申请',
      status: '已通过',
      applicant: '陈思',
      applicantId: 'demo-user-1',
      applyTime: now,
      priority: '普通',
      reason: '细胞房公共耗材周转'
    },
    {
      key: key2,
      title: '冻存管与冰盒',
      category: '耗材申请',
      status: '已通过',
      applicant: '王浩',
      applicantId: 'demo-user-2',
      applyTime: now,
      priority: '紧急',
      reason: '样本库整理急需'
    }
  ];
  return {
    applications,
    statusMap: { [key1]: '待采购', [key2]: '采购中' }
  };
}

export function getDemoReagentProcurement(): {
  applications: ApprovalApplicationExtended[];
  statusMap: Record<string, string>;
} {
  const key1 = 'demo-proc-r-1';
  const applications: ApprovalApplicationExtended[] = [
    {
      key: key1,
      title: '订购 TRIzol 试剂',
      category: '试剂申请',
      status: '已通过',
      applicant: '李薇',
      applicantId: 'demo-user-3',
      applyTime: new Date().toISOString(),
      priority: '普通',
      reason: 'RNA 提取实验批次增加'
    }
  ];
  return { applications: applications, statusMap: { [key1]: '待采购' } };
}

export function getDemoConsumableClaimLogs(): ActivityLog[] {
  return [
    {
      id: 'demo-cl-1',
      type: 'consumable',
      action: 'use',
      message: '陈思 申领 15 mL 离心管 × 2 袋',
      time: '今天 11:20',
      actor: { name: '陈思', username: 'chensi' },
      priority: 'low'
    },
    {
      id: 'demo-cl-2',
      type: 'consumable',
      action: 'use',
      message: '王浩 申领 移液枪头 200μL × 1 盒',
      time: '昨天 16:05',
      actor: { name: '王浩', username: 'wanghao' },
      priority: 'low'
    }
  ];
}

export function getDemoReagentClaimLogs(): ActivityLog[] {
  return [
    {
      id: 'demo-rl-1',
      type: 'reagent',
      action: 'use',
      message: '李薇 领取 DAPI 染液 1 支',
      time: '今天 09:40',
      actor: { name: '李薇', username: 'liwei' },
      priority: 'medium'
    }
  ];
}

export function getDemoTeamProfileRows(): { k: string; v: string }[] {
  return [
    { k: '团队名称', v: '演示团队（示例）' },
    { k: '单位名称', v: '示例大学生命科学学院' },
    { k: '团队规模', v: '20–50 人' },
    { k: '研究领域', v: '细胞与分子生物学' },
    { k: '简介', v: '用于新用户浏览系统各模块的示例团队，数据均为虚构。' },
    { k: '联系邮箱', v: 'lab-demo@example.edu.cn' }
  ];
}

export type DemoRecentSubmission = {
  key: string;
  title: string;
  meta: string;
  status: '待审批' | '已通过' | '已驳回' | '已撤回';
};

export function getDemoRecentSubmissions(): DemoRecentSubmission[] {
  return [
    {
      key: 'demo-app-1',
      title: '耗材申请 · 细胞房公共耗材补充',
      meta: '待审批 · 今天',
      status: '待审批'
    },
    {
      key: 'demo-app-2',
      title: '请假 · 学术会议出差',
      meta: '已通过 · 上周',
      status: '已通过'
    },
    {
      key: 'demo-app-3',
      title: '仪器预约 · 流式上机',
      meta: '待审批 · 昨天',
      status: '待审批'
    },
    {
      key: 'demo-app-4',
      title: '财务报销 · 学术会议差旅',
      meta: '一级审批中 · 本周',
      status: '待审批'
    }
  ];
}

export function getDemoDutyGroups(): DutyGroup[] {
  const m = getDemoMembers().slice(0, 3);
  return [
    {
      _id: 'demo-dg-1',
      name: '公共实验室轮值（演示）',
      description: '加入团队后可维护真实成员组。',
      memberIds: m.map((u) => ({
        _id: String(u._id),
        name: u.name,
        username: u.username,
        studentId: u.studentId,
        avatar: u.avatar
      }))
    }
  ];
}

export function getDemoLabActivities(): LabActivityItem[] {
  const t = dayjs();
  return [
    {
      _id: 'demo-la-1',
      title: '细胞间安全培训（演示）',
      description: '示例活动：加入团队后可发布与报名真实活动。',
      location: '主楼 301 会议室',
      startTime: iso(t.add(3, 'day').hour(14).minute(0).second(0)),
      endTime: iso(t.add(3, 'day').hour(16).minute(0).second(0)),
      createdBy: { _id: 'demo-user-6', name: '张文华', username: 'zhangwenhua' },
      status: 'published',
      createdAt: iso(t.subtract(1, 'day')),
      maxParticipants: 8,
      registrationPendingCount: 2,
      registrationApprovedCount: 5,
      registrationActiveCount: 7,
      isRegistrationFull: false
    },
    {
      _id: 'demo-la-2',
      title: '课题组学术沙龙：免疫治疗前沿',
      description: '演示条目',
      location: '线上腾讯会议',
      startTime: iso(t.add(10, 'day').hour(19).minute(30).second(0)),
      endTime: iso(t.add(10, 'day').hour(21).minute(0).second(0)),
      createdBy: { _id: 'demo-user-1', name: '陈思', username: 'chensi' },
      status: 'published',
      createdAt: iso(t.subtract(5, 'day')),
      maxParticipants: 12,
      registrationPendingCount: 0,
      registrationApprovedCount: 12,
      registrationActiveCount: 12,
      isRegistrationFull: true
    },
    {
      _id: 'demo-la-3',
      title: '大型仪器开放日参观（演示）',
      description: '面向新生的平台介绍与预约演示。',
      location: '公共仪器平台大厅',
      startTime: iso(t.add(18, 'day').hour(9).minute(0).second(0)),
      endTime: iso(t.add(18, 'day').hour(11).minute(30).second(0)),
      createdBy: { _id: 'demo-user-2', name: '王浩', username: 'wanghao' },
      status: 'published',
      createdAt: iso(t.subtract(2, 'day')),
      registrationPendingCount: 1,
      registrationApprovedCount: 8
    },
    {
      _id: 'demo-la-4',
      title: '消防演练与应急疏散（演示）',
      description: '年度例行演练，演示模式下仅作展示。',
      location: '实验楼外广场',
      startTime: iso(t.add(25, 'day').hour(15).minute(0).second(0)),
      endTime: iso(t.add(25, 'day').hour(16).minute(0).second(0)),
      createdBy: { _id: 'demo-user-6', name: '张文华', username: 'zhangwenhua' },
      status: 'published',
      createdAt: iso(t.subtract(8, 'hour')),
      registrationPendingCount: 4,
      registrationApprovedCount: 0
    }
  ];
}

export function getDemoLabActivityById(id: string): LabActivityItem | null {
  return getDemoLabActivities().find((a) => a._id === id) || null;
}

export function getDemoLabTeamParticipation(): { rows: LabTeamParticipationRow[]; generatedAt: string } {
  const m = getDemoMembers().slice(0, 4);
  return {
    generatedAt: iso(dayjs()),
    rows: m.map((u, i) => ({
      userId: String(u._id),
      name: u.name,
      studentId: u.studentId || '',
      studentType: u.studentType || '研究生',
      avatar: u.avatar,
      approvedCount: 3 - (i % 2),
      pendingCount: i % 2,
      rejectedCount: 0,
      approvedActivities: [
        {
          activityId: 'demo-la-1',
          title: '细胞间安全培训（演示）',
          startTime: iso(dayjs().add(3, 'day')),
          activityStatus: 'published'
        }
      ],
      approvedTitlesSummary: '细胞间安全培训（演示）',
      lastParticipatedAt: iso(dayjs().subtract(2, 'day'))
    }))
  };
}

export function getDemoSharedNotebookFolders(): SharedExperimentNotebookFolder[] {
  const t = iso(dayjs());
  return [
    {
      _id: 'demo-sn-folder-1',
      name: '课题组共享笔记（演示）',
      description: '加入真实团队后可创建共享夹并邀请成员。',
      owner: { _id: 'demo-user-6', username: 'zhangwenhua', email: 'pi@demo.lab' },
      sharedUsers: [
        { user: { _id: 'demo-user-1', email: 'chensi@demo.lab' }, email: 'chensi@demo.lab', status: 'joined' }
      ],
      permissions: {
        canAddRecords: true,
        canRemoveRecords: false,
        canViewUsers: true,
        canEditRecords: false
      },
      createdAt: t,
      updatedAt: t
    }
  ];
}

/** 审批管理页：与页面内 ApprovalApplication 结构一致的可展示数据 */
export function getDemoApprovalApplicationsForManagement(): Record<string, unknown>[] {
  const now = new Date().toISOString();
  return [
    {
      key: 'demo-am-1',
      title: '细胞房耗材季度补充',
      category: '耗材申请',
      status: '一级审批中',
      applicant: '陈思',
      applyTime: now,
      priority: '紧急',
      reason: '枪头与培养皿低于安全库存。',
      consumableProjects: [
        {
          image: '',
          name: '15 mL 离心管',
          model: '无菌',
          unit: '袋',
          stockQuantity: '12',
          storageLocation: '库房 A',
          purchaseDate: '2026-01-01',
          purchaseChannel: '网购',
          purchaseLink: '',
          supplier: '—',
          amount: '20',
          price: '',
          total: '',
          usage: '细胞培养日常'
        }
      ]
    },
    {
      key: 'demo-am-2',
      title: '订购限制性内切酶套装',
      category: '试剂申请',
      status: '待审批',
      applicant: '王浩',
      applyTime: now,
      priority: '普通',
      reason: '载体构建实验需要。',
      reagentProjects: [
        {
          category: '公共药品',
          cas: '9001-12-1',
          vendor: 'NEB',
          specification: '20 μL',
          unit: '支',
          stockQuantity: '2',
          storageLocation: '试剂柜',
          purchaseDate: '2025-12-01',
          purchaseChannel: '协议供货',
          amount: '3',
          price: '280',
          total: '840',
          usage: '双酶切'
        }
      ]
    },
    {
      key: 'demo-am-3',
      title: '事假两天',
      category: '请假',
      status: '已通过',
      applicant: '李薇',
      applyTime: now,
      priority: '普通',
      reason: '家中有事。',
      leaveStart: '2026-04-20',
      leaveEnd: '2026-04-21'
    },
    {
      key: 'demo-am-4',
      title: '参加国内学术会议',
      category: '出差申请',
      status: '已驳回',
      applicant: '刘洋',
      applyTime: now,
      priority: '普通',
      reason: '预算未通过。',
      destination: '上海',
      tripStartDate: '2026-05-10',
      tripEndDate: '2026-05-12',
      tripDays: '3',
      tripPurpose: '分会场报告',
      tripBudget: '4500'
    },
    {
      key: 'demo-am-5',
      title: '测试中心上机',
      category: '测试申请',
      status: '已撤回',
      applicant: '赵敏',
      applyTime: now,
      priority: '普通',
      reason: '改送外单位检测。',
      testPlatform: '质谱平台',
      testProjects: [{ project: '代谢组学', unitPrice: '800', quantity: '1', totalAmount: '800' }],
      testTotalAmount: '800'
    },
    {
      key: 'demo-am-6',
      title: '学术会议差旅报销',
      category: '财务报销',
      status: '一级审批中',
      applicant: '周敏',
      applyTime: now,
      priority: '普通',
      reason: '研讨会注册费与城市间交通。',
      totalAmount: 3280.5,
      financeReimbursement: {
        claimAmount: 3280.5,
        currency: 'CNY',
        expenseType: '差旅',
        remark: '研讨会注册费与城市间交通。',
        attachments: [{ name: '发票汇总.pdf', url: '/uploads/approval-attachments/demo-sample.pdf' }]
      }
    }
  ];
}

export function getDemoReservationsForApproval(identity: {
  username: string;
  realName: string;
}): Reservation[] {
  const t = dayjs();
  const rp = [identity.username, identity.realName].filter((s) => String(s || '').trim()).join('、') || '演示用户';
  const ins = {
    _id: 'demo-ins-am-1',
    name: '高分辨质谱',
    model: 'Orbitrap',
    location: '核心仪器室',
    status: '可用' as const,
    responsiblePerson: rp,
    category: '分析仪器'
  };
  return [
    {
      _id: 'demo-resv-1',
      instrumentId: ins,
      userId: { _id: 'demo-user-1', name: '陈思', username: 'chensi', email: 'chensi@demo.lab' },
      startTime: iso(t.add(1, 'day').hour(10).minute(0)),
      endTime: iso(t.add(1, 'day').hour(12).minute(0)),
      purpose: '标样重复测定',
      status: '待审核',
      createdAt: iso(t.subtract(2, 'hour'))
    },
    {
      _id: 'demo-resv-2',
      instrumentId: ins,
      userId: { _id: 'demo-user-2', name: '王浩', username: 'wanghao', email: 'wanghao@demo.lab' },
      startTime: iso(t.add(2, 'day').hour(14).minute(0)),
      endTime: iso(t.add(2, 'day').hour(17).minute(0)),
      purpose: '蛋白样品前处理',
      status: '已批准',
      createdAt: iso(t.subtract(1, 'day'))
    }
  ];
}

export function getDemoLiteratureShareInvites(): LiteratureNotification[] {
  const t = iso(dayjs());
  return [
    {
      _id: 'demo-ln-1',
      type: 'shared_experiment_notebook_invite',
      recipient: 'me',
      sender: { _id: 'demo-user-6', username: 'zhangwenhua', email: 'pi@demo.lab' },
      experimentNotebookFolder: { _id: 'demo-sn-folder-1', name: '课题组共享笔记（演示）', description: '' },
      title: '共享实验记录夹邀请',
      content: '邀请您加入共享夹以便协作整理实验记录。',
      status: 'pending',
      createdAt: t,
      updatedAt: t
    },
    {
      _id: 'demo-ln-2',
      type: 'shared_folder_invite',
      recipient: 'me',
      sender: { _id: 'demo-user-1', username: 'chensi', email: 'chensi@demo.lab' },
      folder: { _id: 'demo-folder-1', name: '文献共享夹（演示）' },
      title: '文献夹共享邀请',
      content: '邀请您共同维护课题文献。',
      status: 'pending',
      createdAt: t,
      updatedAt: t
    }
  ];
}

function emptyMedia(): FeedbackPost['media'] {
  return [];
}

export function getDemoFeedbackPostsAll(): FeedbackPost[] {
  const t = iso(dayjs());
  return [
    {
      _id: 'demo-fb-1',
      author: 'u1',
      anonymousName: '显微镜旁的猫',
      title: '建议：仪器预约页增加「常用时段」筛选',
      content:
        '高峰期经常抢不到共聚焦，希望能按时段筛选空闲仪器，或显示未来一周的可预约热力图。（演示内容）',
      category: '建议',
      tags: ['仪器', '预约'],
      media: emptyMedia(),
      status: 'processing',
      allowComment: true,
      adminOnlyReply: false,
      likeCount: 24,
      disagreeCount: 1,
      commentCount: 6,
      reportCount: 0,
      isHidden: false,
      createdAt: t,
      updatedAt: t
    },
    {
      _id: 'demo-fb-2',
      author: 'u2',
      anonymousName: '离心机守护者',
      title: '吐槽：库房领耗材排队太久',
      content: '工作日下午领耗材平均要等 20 分钟，能否开通线上预约取号？（演示）',
      category: '吐槽/反馈',
      tags: ['耗材', '流程'],
      media: emptyMedia(),
      status: 'pending',
      allowComment: true,
      adminOnlyReply: false,
      likeCount: 15,
      disagreeCount: 2,
      commentCount: 3,
      reportCount: 0,
      isHidden: false,
      createdAt: iso(dayjs().subtract(1, 'day')),
      updatedAt: iso(dayjs().subtract(1, 'day'))
    },
    {
      _id: 'demo-fb-3',
      author: 'u3',
      anonymousName: '夜班实验员',
      title: '求助：细胞房霉菌污染后的标准处置流程',
      content: '第一次遇到疑似霉菌，已暂停实验，想参考其他实验室的 SOP。（演示）',
      category: '求助',
      tags: ['细胞房', '安全'],
      media: emptyMedia(),
      status: 'accepted',
      allowComment: true,
      adminOnlyReply: false,
      likeCount: 31,
      disagreeCount: 0,
      commentCount: 11,
      reportCount: 0,
      isHidden: false,
      createdAt: iso(dayjs().subtract(3, 'day')),
      updatedAt: iso(dayjs().subtract(2, 'day'))
    },
    {
      _id: 'demo-fb-4',
      author: 'u4',
      anonymousName: 'PI 的小助理',
      title: '表扬：值班老师周末响应很快',
      content: '周六晚上仪器报警，值班工程师远程指导处理完毕，非常感谢。（演示）',
      category: '表扬',
      tags: ['服务'],
      media: emptyMedia(),
      status: 'resolved',
      allowComment: true,
      adminOnlyReply: false,
      likeCount: 52,
      disagreeCount: 0,
      commentCount: 4,
      reportCount: 0,
      isHidden: false,
      createdAt: iso(dayjs().subtract(5, 'day')),
      updatedAt: iso(dayjs().subtract(4, 'day'))
    },
    {
      _id: 'demo-fb-5',
      author: 'u5',
      anonymousName: '匿名用户',
      title: '投票讨论：是否统一采购细胞培养血清品牌',
      content: '目前各组血清品牌不一，讨论是否由平台集中采购以压价。（演示）',
      category: '投票讨论',
      tags: ['采购', '细胞'],
      media: emptyMedia(),
      status: 'pending',
      allowComment: true,
      adminOnlyReply: false,
      likeCount: 8,
      disagreeCount: 3,
      commentCount: 18,
      reportCount: 0,
      isHidden: false,
      createdAt: iso(dayjs().subtract(12, 'hour')),
      updatedAt: iso(dayjs().subtract(12, 'hour'))
    }
  ];
}

export function paginateDemoFeedbackPosts(params: {
  page: number;
  pageSize: number;
  category?: string;
  status?: string;
  sort: 'hot' | 'new' | 'pending';
}): FeedbackPostListResult {
  let rows = [...getDemoFeedbackPostsAll()];
  if (params.category) {
    rows = rows.filter((r) => r.category === params.category);
  }
  if (params.status) {
    rows = rows.filter((r) => r.status === params.status);
  }
  if (params.sort === 'pending') {
    rows = rows.filter((r) => r.status === 'pending');
  }
  if (params.sort === 'hot') {
    rows.sort((a, b) => b.likeCount + b.commentCount * 2 - (a.likeCount + a.commentCount * 2));
  } else {
    rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }
  const total = rows.length;
  const start = (params.page - 1) * params.pageSize;
  const items = rows.slice(start, start + params.pageSize);
  return { total, page: params.page, pageSize: params.pageSize, items };
}

export function isDemoFeedbackPostId(id: string | undefined): boolean {
  return Boolean(id && id.startsWith('demo-fb-'));
}

export function getDemoFeedbackPostBundle(postId: string): {
  post: FeedbackPost;
  ticket: FeedbackTicket | null;
  comments: FeedbackComment[];
} | null {
  const post = getDemoFeedbackPostsAll().find((p) => p._id === postId);
  if (!post) return null;
  const t = iso(dayjs());
  const ticket: FeedbackTicket | null =
    post._id === 'demo-fb-1'
      ? {
          _id: 'demo-tk-1',
          post: postId,
          assignee: '管理员',
          status: 'in_progress',
          progressDetail: '已与仪器平台沟通，排期评估中。',
          resolutionSummary: '',
          createdAt: t,
          updatedAt: t
        }
      : post._id === 'demo-fb-4'
        ? {
            _id: 'demo-tk-4',
            post: postId,
            assignee: '管理员',
            status: 'resolved',
            progressDetail: '已转达值班团队并记录在案。',
            resolutionSummary: '感谢反馈，后续将纳入季度服务评优。',
            resolvedAt: t,
            createdAt: t,
            updatedAt: t
          }
        : null;
  const comments: FeedbackComment[] =
    post._id === 'demo-fb-1'
      ? [
          {
            _id: 'demo-fbc-1',
            post: postId,
            author: 'admin',
            parent: null,
            anonymousName: '平台小助手',
            content: '收到，我们会在下周产品例会上评估该需求。',
            isOfficial: true,
            likeCount: 5,
            reportCount: 0,
            status: 'visible',
            createdAt: iso(dayjs().subtract(6, 'hour')),
            updatedAt: iso(dayjs().subtract(6, 'hour'))
          },
          {
            _id: 'demo-fbc-2',
            post: postId,
            author: 'u9',
            parent: null,
            anonymousName: '路人甲',
            content: '+1，热力图会很好用。',
            isOfficial: false,
            likeCount: 2,
            reportCount: 0,
            status: 'visible',
            createdAt: iso(dayjs().subtract(4, 'hour')),
            updatedAt: iso(dayjs().subtract(4, 'hour'))
          }
        ]
      : [];
  return { post, ticket, comments };
}

export function getDemoMyFeedbackBundle(): {
  posts: FeedbackPostListResult;
  comments: FeedbackCommentListResult;
  reports: { items: unknown[] };
} {
  const posts = getDemoFeedbackPostsAll().slice(0, 2);
  return {
    posts: { total: posts.length, page: 1, pageSize: 100, items: posts },
    comments: {
      total: 1,
      page: 1,
      pageSize: 100,
      items: [
        {
          _id: 'demo-my-c-1',
          post: 'demo-fb-1',
          author: 'me',
          parent: null,
          anonymousName: '我',
          content: '补充：我们组也有同样诉求。',
          isOfficial: false,
          likeCount: 0,
          reportCount: 0,
          status: 'visible',
          createdAt: iso(dayjs().subtract(1, 'day')),
          updatedAt: iso(dayjs().subtract(1, 'day'))
        }
      ]
    },
    reports: {
      items: [
        {
          _id: 'demo-rp-1',
          reasonType: '不实信息',
          status: 'pending',
          reasonDetail: '演示：某匿名帖内容疑似夸大。',
          createdAt: iso(dayjs().subtract(2, 'day'))
        }
      ]
    }
  };
}
